import { IClauseChecklist } from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import AddIcon from "@mui/icons-material/Add";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import SaveIcon from "@mui/icons-material/Save";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { AddClauseToChecklistDialog } from "../dialogs/AddClauseToChecklistDialog";
import { AddTopicsToChecklistDialog } from "../dialogs/AddTopicsToChecklistDialog";

interface IProps {
    /**
     * The clause checklist
     */
    clauseChecklist: IClauseChecklist;
    /**
     * Whether there are unsaved changes.
     */
    hasChanges?: boolean;
    /**
     * Whether save is in progress.
     */
    isSaving?: boolean;
    /**
     * Handler for save action.
     */
    onSave?: () => void;
}

/**
 * The toolbar for the topics section in a clause checklist.
 */
export const ClauseChecklistTopicsToolbar = ({
    clauseChecklist,
    hasChanges = false,
    isSaving = false,
    onSave,
}: IProps) => {
    const [addClauseModalOpen, setAddClauseModalOpen] = useState(false);
    const [addTopicsModalOpen, setAddTopicsModalOpen] = useState(false);

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
        >
            {/* Topics Header - LEFT SIDE */}
            <Typography variant="h6" fontWeight={700}>
                Topics
            </Typography>

            {/* Actions - RIGHT SIDE */}
            <Stack direction="row" spacing={1}>
                {/* Save Changes Button */}
                {onSave && (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={
                            isSaving ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <SaveIcon />
                            )
                        }
                        onClick={onSave}
                        disabled={!hasChanges || isSaving}
                        disableElevation
                        sx={{
                            borderRadius: 29,
                        }}
                    >
                        {isSaving ? "Saving..." : "Save Changes"}
                    </Button>
                )}

                {/* Add Clause Button */}
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PlaylistAddIcon />}
                    onClick={() => setAddClauseModalOpen(true)}
                    disableElevation
                    sx={{
                        borderRadius: 29,
                    }}
                >
                    Add Clause
                </Button>

                {/* Add Topics Button */}
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setAddTopicsModalOpen(true)}
                    disableElevation
                    sx={{
                        borderRadius: 29,
                    }}
                >
                    Add Topics
                </Button>
            </Stack>

            {/* Add Clause Modal */}
            <AddClauseToChecklistDialog
                open={addClauseModalOpen}
                onClose={() => setAddClauseModalOpen(false)}
                clauseChecklist={clauseChecklist}
            />

            {/* Add Topics Modal */}
            <AddTopicsToChecklistDialog
                open={addTopicsModalOpen}
                onClose={() => setAddTopicsModalOpen(false)}
                clauseChecklist={clauseChecklist}
            />
        </Stack>
    );
};
